/**
 * Consensus tally THUẦN — weighted vote + veto + quorum-min (US-E17.F2-0189-S02 ·
 * @UC9254 @UC9255 @UC9256 @UC9257) — @gate consensus.
 *
 * Không DB, không framework, deterministic. Trích + MỞ RỘNG logic đang inline trong
 * `VoteService._tally`/`_earlyDecision`/`_finalizeInTx` (đếm ĐẦU NGƯỜI) thành hàm thuần dùng chung
 * ⇒ early ≡ final (một công thức duy nhất).
 *
 * LUẬT (THỨ TỰ BẤT BIẾN — veto → quorum → threshold):
 *  1. `vetoNo` (bất kỳ phiếu `no` từ member có quyền phủ quyết) ⇒ **failed NGAY** (thắng mọi tỉ lệ).
 *  2. `participants < quorumMin`:
 *       - finalize (hết hạn) ⇒ **failed** (VOTE-NO-QUORUM-FAIL — phiên không hợp lệ dù yes > no).
 *       - chưa finalize ⇒ **open** (chưa đủ người tham gia thì KHÔNG chốt sớm passed).
 *     `abstain` **TÍNH** vào participants (có tham gia) nhưng KHÔNG vào tử/mẫu tỉ-lệ.
 *  3. threshold theo mechanism, tính trên **TRỌNG SỐ** (không đầu người):
 *       - `majority`: `yesWeight * 2 > totalEligibleWeight` ⇒ passed;
 *                     `noWeight * 2 >= totalEligibleWeight` ⇒ failed (không thể quá bán nữa).
 *       - `ratio`:    nhân chéo `yesWeight ≥ thr * totalEligibleWeight` (so sánh integer bằng cách
 *                     nhân 2 vế với mẫu số cố định — KHÔNG chia, tránh float drift).
 *       - `silent`:   `noWeight === 0` + finalize ⇒ passed; có `no` ⇒ failed; else open.
 *
 * KHÔNG REGRESS: weight=1 mọi phiếu + quorumMin=0 + không veto ⇒ công thức rút gọn về ĐÚNG hành vi
 * đếm-đầu-người cũ (`totalEligibleWeight` = `eligibleCount`).
 *
 * NO-FLOAT: weight là **Int**. `thresholdRatio` là số thực cấu hình (0-1) — chỉ dùng trong 1 phép
 * nhân chéo có LÀM TRÒN LÊN bằng scale integer (RATIO_SCALE), không tích luỹ sai số.
 *
 * FAIL-SAFE: weight bẩn (âm / float / NaN) ⇒ về `DEFAULT_VOTE_WEIGHT` (1) — KHÔNG tước phiếu, KHÔNG
 * cho phiếu ma. `totalEligibleWeight ≤ 0` ⇒ failed (fail-closed, không chia 0).
 */

/** Trọng số mặc định — 1 người 1 phiếu (hành vi CŨ). */
export const DEFAULT_VOTE_WEIGHT = 1;

/** Trần trọng số 1 phiếu — P3.2 chống "con tin ngược": 1 người giàu KHÔNG áp đảo tuyệt đối. */
export const MAX_VOTE_WEIGHT = 10;

/** Nguồn tính trọng số phiếu (snapshot lúc mở phiên). */
export const VOTE_WEIGHT_SOURCES = ["equal", "by_role", "by_contribution"] as const;
export type VoteWeightSource = (typeof VOTE_WEIGHT_SOURCES)[number];

/** Nguồn mặc định = `equal` ⇒ quỹ cũ 0 regress. */
export const DEFAULT_VOTE_WEIGHT_SOURCE: VoteWeightSource = "equal";

/** Trọng số theo VAI TRÒ (by_role). Role lạ ⇒ mặc định 1 (fail-safe). */
export const ROLE_VOTE_WEIGHTS: Readonly<Record<string, number>> = {
  owner: 2,
  treasurer: 2,
  admin: 2,
  member: 1,
};

/** 1 đơn vị trọng số / mức đóng góp này (minor) — mặc định by_contribution. */
export const DEFAULT_CONTRIBUTION_WEIGHT_UNIT_MINOR = 100_000n;

/** Mẫu số cố định để so sánh tỉ lệ bằng SỐ NGUYÊN (nhân chéo, không chia). */
const RATIO_SCALE = 1_000_000;

export interface WeightedVote {
  readonly choice: "yes" | "no" | "abstain";
  readonly weight: number;
  readonly hasVeto: boolean;
}

export interface WeightedTally {
  readonly yesWeight: number;
  readonly noWeight: number;
  readonly abstainWeight: number;
  /** ĐẦU NGƯỜI đã bỏ phiếu (gồm abstain) — mẫu số của quorum-min. */
  readonly participants: number;
  /** Có ít nhất 1 phiếu `no` từ member có quyền phủ quyết. */
  readonly vetoNo: boolean;
}

export interface DecisionRule {
  readonly mechanism: "majority" | "ratio" | "silent";
  readonly thresholdRatio: number | null;
  /** Số NGƯỜI tham gia tối thiểu (0 = không chặn). Tách hoàn toàn khỏi threshold. */
  readonly quorumMin: number;
  /** Σ trọng số toàn bộ member đủ điều kiện — SNAPSHOT lúc mở phiên (chống lách người). */
  readonly totalEligibleWeight: number;
}

export type DecisionVerdict = "passed" | "failed" | "open";

/** True nếu `value` là nguồn trọng số hợp lệ (type-guard cho contract/DB). */
export function isVoteWeightSource(value: unknown): value is VoteWeightSource {
  return (
    typeof value === "string" && (VOTE_WEIGHT_SOURCES as readonly string[]).includes(value)
  );
}

/** Chuẩn hoá weight: Int ≥ 1, trần MAX_VOTE_WEIGHT. Bẩn (âm/float/NaN) ⇒ 1 (fail-safe). */
export function normalizeWeight(weight: unknown): number {
  if (typeof weight !== "number" || !Number.isInteger(weight) || weight < DEFAULT_VOTE_WEIGHT) {
    return DEFAULT_VOTE_WEIGHT;
  }
  return Math.min(weight, MAX_VOTE_WEIGHT);
}

export interface MemberWeightInput {
  readonly role: string;
  /** Tổng đã đóng góp (minor, BigInt — B1 no-float) cho `by_contribution`. */
  readonly contributionMinor: bigint;
}

/**
 * Trọng số của 1 member theo `source` — **Int**, sàn 1, trần `MAX_VOTE_WEIGHT`.
 * `by_contribution`: `floor(contribution / unit)` (đóng góp 0 vẫn CÓ 1 phiếu — không tước quyền).
 */
export function memberVoteWeight(
  source: VoteWeightSource,
  member: MemberWeightInput,
  contributionUnitMinor: bigint = DEFAULT_CONTRIBUTION_WEIGHT_UNIT_MINOR,
): number {
  if (source === "by_role") {
    return normalizeWeight(ROLE_VOTE_WEIGHTS[member.role] ?? DEFAULT_VOTE_WEIGHT);
  }
  if (source === "by_contribution") {
    if (typeof contributionUnitMinor !== "bigint" || contributionUnitMinor <= 0n) {
      return DEFAULT_VOTE_WEIGHT; // config bẩn ⇒ equal (fail-safe, không tước phiếu)
    }
    const c = typeof member.contributionMinor === "bigint" ? member.contributionMinor : 0n;
    if (c <= 0n) return DEFAULT_VOTE_WEIGHT;
    const units = c / contributionUnitMinor; // BigInt division = floor (no-float)
    const capped = units > BigInt(MAX_VOTE_WEIGHT) ? BigInt(MAX_VOTE_WEIGHT) : units;
    return normalizeWeight(Number(capped));
  }
  return DEFAULT_VOTE_WEIGHT; // equal
}

/** Cộng phiếu theo TRỌNG SỐ + đếm đầu người (quorum) + phát hiện veto-no. THUẦN. */
export function tallyWeighted(votes: readonly WeightedVote[]): WeightedTally {
  let yesWeight = 0;
  let noWeight = 0;
  let abstainWeight = 0;
  let participants = 0;
  let vetoNo = false;

  for (const vote of votes) {
    const w = normalizeWeight(vote.weight);
    participants += 1; // abstain CÓ tham gia (vào quorum)
    if (vote.choice === "yes") yesWeight += w;
    else if (vote.choice === "no") {
      noWeight += w;
      if (vote.hasVeto === true) vetoNo = true; // veto CHỈ kích hoạt bởi phiếu `no`
    } else abstainWeight += w;
  }

  return { yesWeight, noWeight, abstainWeight, participants, vetoNo };
}

/**
 * Verdict cho 1 bộ phiếu. `finalize=true` khi deadline qua (không còn phiếu mới) ⇒ chốt pass/fail;
 * `false` (mặc định) = quyết định SỚM ⇒ chỉ chốt khi CHẮC CHẮN, else `open`.
 * DÙNG CHUNG cho `_earlyDecision` và `_finalizeInTx` ⇒ không thể lệch nhau.
 */
export function evaluateDecision(
  tally: WeightedTally,
  rule: DecisionRule,
  options: { readonly finalize?: boolean } = {},
): DecisionVerdict {
  const finalize = options.finalize === true;
  const total = rule.totalEligibleWeight;

  // 1. VETO — thắng mọi tỉ lệ, mọi quorum, mọi lúc.
  if (tally.vetoNo === true) return "failed";

  // Fail-closed: mẫu số hỏng ⇒ failed (không chia 0, không tự chi).
  if (!Number.isInteger(total) || total <= 0) return "failed";

  // 2. QUORUM-MIN (tách khỏi threshold) — abstain TÍNH là tham gia.
  const quorumMin = Number.isInteger(rule.quorumMin) && rule.quorumMin > 0 ? rule.quorumMin : 0;
  if (tally.participants < quorumMin) {
    return finalize ? "failed" : "open"; // hết hạn mà thiếu người ⇒ phiên vô hiệu
  }

  // 3. THRESHOLD trên TRỌNG SỐ.
  const yes = tally.yesWeight;
  const no = tally.noWeight;
  // Trọng số CHƯA bỏ phiếu (bao gồm cả abstain đã bỏ — abstain KHÔNG bao giờ thành yes nữa).
  const votedWeight = yes + no + tally.abstainWeight;
  const remaining = Math.max(total - votedWeight, 0);

  if (rule.mechanism === "silent") {
    if (no > 0) return "failed"; // có phản đối ⇒ không im-lặng-duyệt được
    return finalize ? "passed" : "open"; // im lặng tới hết hạn ⇒ passed
  }

  if (rule.mechanism === "majority") {
    // Nhân chéo (integer) thay cho `yes > total/2` — quá bán TUYỆT ĐỐI ⇒ chốt SỚM (không thể lật).
    if (yes * 2 > total) return "passed";
    if (no * 2 >= total) return "failed"; // yes không thể quá bán nữa
    // Hết hạn mà chưa ai quá bán tuyệt đối ⇒ so tương đối trên TRỌNG SỐ (mirror `_finalizeInTx` CŨ:
    // `tally.yes > tally.no`). Hoà / thua ⇒ failed. GIỮ NGUYÊN hành vi legacy (0 regress).
    if (finalize) return yes > no ? "passed" : "failed";
    return "open";
  }

  // ratio — nhân chéo integer: yes ≥ thr × total  ⇔  yes × SCALE ≥ round-up(thr × SCALE) × total
  const thr = typeof rule.thresholdRatio === "number" && Number.isFinite(rule.thresholdRatio)
    ? rule.thresholdRatio
    : 0.5;
  const thrScaled = Math.ceil(thr * RATIO_SCALE); // ngưỡng làm tròn LÊN ⇒ không "gần đủ mà pass"
  const needScaled = thrScaled * total; // = thr × total, thang RATIO_SCALE
  if (yes * RATIO_SCALE >= needScaled) return "passed";
  if ((yes + remaining) * RATIO_SCALE < needScaled) return "failed"; // không đủ phiếu còn lại
  if (finalize) return "failed";
  return "open";
}
